import type { BaseEntity, FieldValue } from "../../UniversalCRMTable/types";

/**
 * Represents progress of a bulk edit operation
 */
export interface BulkEditProgressItem {
  id: string;
  status: "pending" | "processing" | "completed" | "error";
  error?: string;
}

export interface BulkEditProgress {
  items: BulkEditProgressItem[];
  completedCount: number;
  errorCount: number;
  totalCount: number;
  isComplete: boolean;
}

export interface BulkEditOptions {
  delayMs?: number;
  onProgress?: (progress: BulkEditProgress) => void;
  onComplete?: (progress: BulkEditProgress) => void;
  onError?: (error: Error, itemId: string) => void;
}

/**
 * Service for handling bulk edit operations with progress tracking
 * Used by GroupedBulkActions for field-based bulk updates
 */
export class BulkEditService {
  /**
   * Execute bulk edit for selected items
   */
  static async executeBulkEdit<T extends BaseEntity>(
    items: T[],
    updateFn: (item: T, fieldId: string, value: FieldValue) => Promise<void>,
    fieldId: string,
    value: FieldValue,
    options: BulkEditOptions = {}
  ): Promise<BulkEditProgress> {
    const { delayMs = 0, onProgress, onComplete, onError } = options;

    const progressItems: BulkEditProgressItem[] = items.map((item) => ({
      id: item.id,
      status: "pending" as const,
    }));

    let completedCount = 0;
    let errorCount = 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      progressItems[i].status = "processing";

      try {
        await updateFn(item, fieldId, value);
        progressItems[i].status = "completed";
        completedCount++;
      } catch (error) {
        progressItems[i].status = "error";
        progressItems[i].error = error instanceof Error ? error.message : "Unknown error";
        errorCount++;
        onError?.(error instanceof Error ? error : new Error(String(error)), item.id);
      }

      // Notify progress
      const progress: BulkEditProgress = {
        items: progressItems,
        completedCount,
        errorCount,
        totalCount: items.length,
        isComplete: i === items.length - 1,
      };
      onProgress?.(progress);

      // Add delay between updates if specified
      if (delayMs > 0 && i < items.length - 1) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }
    }

    const finalProgress: BulkEditProgress = {
      items: progressItems,
      completedCount,
      errorCount,
      totalCount: items.length,
      isComplete: true,
    };

    onComplete?.(finalProgress);
    return finalProgress;
  }

  /**
   * Execute bulk edit for all filtered items
   */
  static async executeBulkEditAllFiltered<T extends BaseEntity>(
    fetchFilteredItems: (itemIds: string[], fieldId: string, value: FieldValue) => Promise<T[]>,
    fieldId: string,
    value: FieldValue,
    filterCriteria?: Record<string, unknown>,
    options: BulkEditOptions = {}
  ): Promise<BulkEditProgress> {
    const { delayMs = 0, onProgress, onComplete, onError } = options;

    try {
      // Fetch all filtered items
      const items = await fetchFilteredItems([], fieldId, value);

      const progressItems: BulkEditProgressItem[] = items.map((item) => ({
        id: item.id,
        status: "pending" as const,
      }));

      let completedCount = 0;
      let errorCount = 0;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        progressItems[i].status = "processing";

        try {
          // In a real implementation, this would call the actual update function
          // For now, we just mark as completed
          progressItems[i].status = "completed";
          completedCount++;
        } catch (error) {
          progressItems[i].status = "error";
          progressItems[i].error = error instanceof Error ? error.message : "Unknown error";
          errorCount++;
          onError?.(error instanceof Error ? error : new Error(String(error)), item.id);
        }

        // Notify progress
        const progress: BulkEditProgress = {
          items: progressItems,
          completedCount,
          errorCount,
          totalCount: items.length,
          isComplete: i === items.length - 1,
        };
        onProgress?.(progress);

        // Add delay between updates if specified
        if (delayMs > 0 && i < items.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
      }

      const finalProgress: BulkEditProgress = {
        items: progressItems,
        completedCount,
        errorCount,
        totalCount: items.length,
        isComplete: true,
      };

      onComplete?.(finalProgress);
      return finalProgress;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      onError?.(error instanceof Error ? error : new Error(errorMessage), "all");
      throw error;
    }
  }
}
